/**
 * Rozszerzenie Temperatio na karcie kartoteki wspólnej (Temperatio A+, migracja 839; 25–26.10.2026).
 *
 * Członkostwo karty w Temperatio liczy WYŁĄCZNIE baza — funkcja `waterandice_w_temperatio(products)`, czytana
 * przez panele jako pole wyliczane PostgREST (`select=*,waterandice_w_temperatio`). Ten plik nie liczy reguły
 * drugi raz: niesie stałe, których potrzebuje formularz, zanim karta w bazie w ogóle powstanie (wartość startowa
 * przełącznika „W Temperatio” przy zakładaniu karty) albo zanim pisarz odpowie (zakresy pól).
 *
 * Każda stała ma bliźniaka w SQL i bramkę, która porównuje je w STANIE KOŃCOWYM migracji
 * (`tests/unit/apps/produktyTemperatioJednaDefinicja.test.ts`, część (c)) — druga kopia reguły bez porównania
 * to dokładnie ten rozjazd, przez który kartoteka centralna i panel Temperatio miały już dwa różne edytory celu
 * (plan `docs/10-TEMPERATIO-NATIVE/11-PRODUKTY-Z-KARTOTEKI-WSPOLNEJ.md`, §4.5).
 *
 * Wartość, nie typ — `@insolitum/ui` ma indeks bez sufiksów `.js`, więc aplikacja Next może ją importować
 * (CLAUDE.md, „`tsc --noEmit` NIE jest dowodem, że `next build` przejdzie”).
 */

import { powodOdmowy } from './powodOdmowy';

/**
 * Moduły zakładające (`products.module_type`), których karty są w Temperatio bez znacznika człowieka.
 * Bliźniak: `IN (…)` w `waterandice_w_temperatio`. `pos` (karty kasy) — decyzja właściciela z 26.10.2026
 * („wszystkie automatycznie”, §10 p. 2 planu, wbrew rekomendacji „poza”).
 */
export const MODULY_DOMYSLNIE_W_TEMPERATIO: readonly string[] = Object.freeze([
  'waterandice',
  'fabrica',
  'pos',
]);

/** Zakres wartości pola liczbowego rozszerzenia; obie granice należą do zakresu. */
export interface ZakresPola {
  readonly min: number;
  readonly max: number;
}

/** Temperatura docelowa ciasta [°C] — pusta znaczy „nie ustawiono”, nigdy 24 °C. Bliźniak: pisarz 839. */
export const ZAKRES_CELU_C: ZakresPola = Object.freeze({ min: 15, max: 35 });

/**
 * Współczynnik tarcia kalkulatora ręcznego [°C], klucz karty `module_data.waterandice.friction_factor`.
 * Zero jest poprawne; zalecenia mieszarek to 1–3 °C. Bliźniak: pisarz 839.
 */
export const ZAKRES_TARCIA_C: ZakresPola = Object.freeze({ min: 0, max: 10 });

/**
 * Korekta miesienia partii z Receptora [K], klucz karty `module_data.waterandice.korekta_miesienia_k` — czyta go
 * wyłącznie wyjście kolejki (`waterandice_core_kolejka_receptora`). Zakres = pole ekranu „Woda” Androida
 * (`WodaZKolejki.kt`, `ZAKRES_KOREKTY`). Bliźniak: pisarz 839.
 */
export const ZAKRES_KOREKTY_K: ZakresPola = Object.freeze({ min: -5, max: 30 });

// ── Logika formularzy (część 2 implementacji, 26.10.2026) ─────────────────────────────────────────────────────

/** Karta kartoteki w zakresie, który czytają ekrany Temperatio (wiersz `products`). */
export interface KartaTemperatio {
  module_type?: string | null;
  module_data?: Record<string, unknown> | null;
  target_dough_temp_c?: number | null;
  slow_mixing_time?: number | null;
  fast_mixing_time?: number | null;
  requires_preferment?: boolean | null;
  fabrica_recipe_stage_id?: string | null;
}

/**
 * Czy kartę założono w Temperatio (`module_type` = pochodzenie karty, nie członkostwo). Tylko na takiej karcie
 * panel Temperatio zmienia atrybuty CAŁEJ karty — nazwę, kategorię, opis, aktywność. Kartę Receptora chroni 807
 * (odmowa 23001), kartę kasy nadpisuje synchronizacja GoPOS przy każdym przebiegu, więc edycja tam byłaby drugą
 * prawdą o karcie albo zmianą, która znika (plan §4.3).
 *
 * JEDYNE miejsce w kodzie klientów, które porównuje `module_type` z Temperatio — bramka
 * `produktyTemperatioJednaDefinicja.test.ts` (a) nie wpuszcza drugiego.
 */
export function zalozonaWTemperatio(karta: { module_type?: string | null }): boolean {
  return karta.module_type === 'waterandice';
}

/** Gałąź `module_data.<klucz>`, jeśli jest obiektem — inaczej `null` (karta „POLNY” ma `module_data IS NULL`). */
function galaz(karta: KartaTemperatio, klucz: string): Record<string, unknown> | null {
  const md = karta.module_data;
  if (!md || typeof md !== 'object' || Array.isArray(md)) return null;
  const g = md[klucz];
  return g && typeof g === 'object' && !Array.isArray(g) ? (g as Record<string, unknown>) : null;
}

/** Karta przyszła z Receptora (gałąź `module_data.receptor`) — znacznik na liście i zdanie o celu z receptury. */
export function kartaZReceptora(karta: KartaTemperatio): boolean {
  return galaz(karta, 'receptor') !== null;
}

/** Skąd karta przyszła — do podpisu w oknie „Dodaj z kartoteki”. */
export type ZrodloKarty = 'receptor' | 'kasa' | 'panel';

export function zrodloKarty(karta: KartaTemperatio): ZrodloKarty {
  if (kartaZReceptora(karta)) return 'receptor';
  if (karta.module_type === 'pos') return 'kasa';
  return 'panel';
}

/** Liczba z gałęzi `module_data.waterandice` — wyłącznie typu liczbowego (tak samo czyta ją baza, 839). */
function liczbaRozszerzenia(karta: KartaTemperatio, klucz: string): number | null {
  const v = galaz(karta, 'waterandice')?.[klucz];
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/** Tarcie kalkulatora ręcznego z karty (`friction_factor`) albo `null` — nie ustawiono. */
export function tarcieKarty(karta: KartaTemperatio): number | null {
  return liczbaRozszerzenia(karta, 'friction_factor');
}

/** Korekta miesienia partii z Receptora z karty (`korekta_miesienia_k`) albo `null` — nie ustawiono. */
export function korektaKarty(karta: KartaTemperatio): number | null {
  return liczbaRozszerzenia(karta, 'korekta_miesienia_k');
}

/**
 * Formularz ustawień Temperatio. Pola liczbowe trzymają SUROWY napis z pola: pusty znaczy „nie ustawiono”
 * i jedzie do pisarza jako `null` — nigdy jako 24 °C ani 0 (plan §1: „puste znaczy nie ustawiono”).
 */
export interface FormularzRozszerzenia {
  target_dough_temp_c: string;
  slow_mixing_time: string;
  fast_mixing_time: string;
  requires_preferment: boolean;
  fabrica_recipe_stage_id: string;
  friction_factor: string;
  korekta_miesienia_k: string;
}

/** Wartość startowa celu NOWEJ karty — propozycja widoczna w polu, nie podstawienie przy zapisie (plan §4.4). */
export const PROPOZYCJA_CELU_NOWEJ_KARTY_C = 24;

const naPole = (v: number | null | undefined): string =>
  typeof v === 'number' && Number.isFinite(v) ? String(v) : '';

/** Formularz z karty (edycja) albo formularz nowej karty (`null`). */
export function formularzRozszerzenia(karta: KartaTemperatio | null): FormularzRozszerzenia {
  if (!karta) {
    return {
      target_dough_temp_c: String(PROPOZYCJA_CELU_NOWEJ_KARTY_C),
      slow_mixing_time: '',
      fast_mixing_time: '',
      requires_preferment: false,
      fabrica_recipe_stage_id: '',
      friction_factor: '',
      korekta_miesienia_k: '',
    };
  }
  return {
    target_dough_temp_c: naPole(karta.target_dough_temp_c),
    slow_mixing_time: naPole(karta.slow_mixing_time),
    fast_mixing_time: naPole(karta.fast_mixing_time),
    requires_preferment: karta.requires_preferment === true,
    fabrica_recipe_stage_id: karta.fabrica_recipe_stage_id ?? '',
    friction_factor: naPole(tarcieKarty(karta)),
    korekta_miesienia_k: naPole(korektaKarty(karta)),
  };
}

/**
 * Wartość pola liczbowego dla pisarza: pusty → `null` („nie ustawiono”), liczba (także z przecinkiem) → liczba.
 * Napis, który liczbą nie jest, jedzie BEZ ZMIAN — pisarz odmówi go zdaniem „to liczba albo puste pole” (22023).
 * Zamiana na `null` albo `NaN` byłaby gorsza: `JSON.stringify(NaN)` to `null`, czyli literówka w polu po cichu
 * kasowałaby ustawienie karty.
 */
export function liczbaZPola(pole: string): number | string | null {
  const t = pole.trim();
  if (t === '') return null;
  const n = Number(t.replace(',', '.'));
  return Number.isFinite(n) ? n : t;
}

const POLA_LICZBOWE = [
  'target_dough_temp_c',
  'slow_mixing_time',
  'fast_mixing_time',
  'friction_factor',
  'korekta_miesienia_k',
] as const;

/**
 * Ładunek pisarza `waterandice_produkt_zapisz` (`p_zmiany`): WYŁĄCZNIE pola zmienione względem formularza
 * z chwili otwarcia (`przed`). Pisarz zapisuje tylko klucze obecne w ładunku, więc pole nietknięte zostaje
 * w bazie takie, jakie jest — także wtedy, gdy w międzyczasie zmienił je ktoś inny. Pusty wynik = nic do
 * zapisania (pisarz pustej zmiany odmawia, panel jej nie wysyła). Nigdy nazwa, kategoria, opis, aktywność,
 * `module_type` ani członkostwo — to nie są pola tego formularza.
 */
export function zmianyRozszerzenia(
  przed: FormularzRozszerzenia,
  po: FormularzRozszerzenia
): Record<string, unknown> {
  const zmiany: Record<string, unknown> = {};
  for (const pole of POLA_LICZBOWE) {
    if (po[pole].trim() !== przed[pole].trim()) zmiany[pole] = liczbaZPola(po[pole]);
  }
  if (po.requires_preferment !== przed.requires_preferment) {
    zmiany.requires_preferment = po.requires_preferment;
  }
  if (po.fabrica_recipe_stage_id !== przed.fabrica_recipe_stage_id) {
    zmiany.fabrica_recipe_stage_id = po.fabrica_recipe_stage_id || null;
  }
  return zmiany;
}

/** Argumenty RPC pisarza — organizacja zawsze jawnie (RLS przepuszcza super-admina do każdej, plan §2.3). */
export function argumentyPisarza(
  organizacjaId: string,
  kartaId: string,
  zmiany: Record<string, unknown>
): { p_organization_id: string; p_product_id: string; p_zmiany: Record<string, unknown> } {
  return { p_organization_id: organizacjaId, p_product_id: kartaId, p_zmiany: zmiany };
}

/**
 * Zdanie odmowy pisarza rozszerzenia dla człowieka. Pisarz odmawia zdaniami po polsku: 22023 (pole spoza listy,
 * zły typ, zakres) i P0002 („Nie ma takiej karty albo nie masz prawa jej zmieniać.”); ochrona kartoteki — 23001
 * (`powodOdmowy`). Każdy inny błąd (sieć, uprawnienia, wyzwalacz bez zdania) — zdanie zapasowe ekranu.
 */
export function zdaniePisarza(blad: unknown, zapas: string): string {
  if (typeof blad === 'object' && blad !== null) {
    const { code, message } = blad as { code?: unknown; message?: unknown };
    if (
      (code === '22023' || code === 'P0002') &&
      typeof message === 'string' &&
      message.trim() !== ''
    ) {
      return message.trim();
    }
  }
  return powodOdmowy(blad, zapas);
}

/**
 * Czy tekst pasuje do frazy wyszukiwania: bez wielkości liter, z polskimi znakami („chłop” trafia
 * w „CHLEB CHŁOPSKI DUŻA FOREMKA”). Pusta fraza pasuje do wszystkiego.
 */
export function pasujeDoFrazy(tekst: string | null | undefined, fraza: string): boolean {
  const f = fraza.trim().toLocaleLowerCase('pl-PL');
  if (f === '') return true;
  return (tekst ?? '').toLocaleLowerCase('pl-PL').includes(f);
}
